/**
 * The viewer's side of the WebRTC session.
 *
 * The host always offers (it owns the tracks); the viewer answers. ICE is
 * trickled both ways through the signaling server. Kept out of the Svelte
 * component so the page only deals with "a stream arrived" and "here are the
 * numbers".
 */
import type { IceServer, SignalPayload } from './protocol'

export type SendSignal = (payload: SignalPayload) => void

export interface ViewerCallbacks {
  onStream: (stream: MediaStream) => void
  onState: (state: RTCPeerConnectionState) => void
}

export class ViewerConnection {
  readonly pc: RTCPeerConnection
  readonly #send: SendSignal
  /** Candidates that arrive before the offer has been applied. */
  #pending: RTCIceCandidateInit[] = []

  constructor(iceServers: IceServer[], send: SendSignal, callbacks: ViewerCallbacks) {
    this.#send = send
    this.pc = new RTCPeerConnection({
      iceServers: iceServers.map((server) => ({
        urls: server.urls,
        ...(server.username ? { username: server.username } : {}),
        ...(server.credential ? { credential: server.credential } : {}),
      })),
    })

    const stream = new MediaStream()
    this.pc.ontrack = (event) => {
      stream.addTrack(event.track)
      // Video favors low latency. Leave audio buffering to the browser's
      // adaptive jitter buffer so uneven packet delivery does not cause gaps.
      const receiver = event.receiver as RTCRtpReceiver & {
        jitterBufferTarget?: number | null
        playoutDelayHint?: number
      }
      if (event.track.kind === 'video') {
        if ('jitterBufferTarget' in receiver) receiver.jitterBufferTarget = 0
        if ('playoutDelayHint' in receiver) receiver.playoutDelayHint = 0
      }
      callbacks.onStream(stream)
    }
    this.pc.onicecandidate = (event) => {
      if (!event.candidate) return
      const init = event.candidate.toJSON()
      this.#send({
        type: 'ice_candidate',
        candidate: init.candidate ?? '',
        ...(init.sdpMid != null ? { sdp_mid: init.sdpMid } : {}),
        ...(init.sdpMLineIndex != null ? { sdp_mline_index: init.sdpMLineIndex } : {}),
      })
    }
    this.pc.onconnectionstatechange = () => callbacks.onState(this.pc.connectionState)
  }

  /** Apply one signal from the host. */
  async handle(payload: SignalPayload): Promise<void> {
    switch (payload.type) {
      case 'offer': {
        await this.pc.setRemoteDescription({ type: 'offer', sdp: payload.sdp })
        const answer = await this.pc.createAnswer()
        await this.pc.setLocalDescription(answer)
        this.#send({ type: 'answer', sdp_type: 'answer', sdp: answer.sdp ?? '' })
        for (const candidate of this.#pending.splice(0)) {
          await this.pc.addIceCandidate(candidate)
        }
        break
      }
      case 'ice_candidate': {
        const candidate: RTCIceCandidateInit = {
          candidate: payload.candidate,
          sdpMid: payload.sdp_mid ?? null,
          sdpMLineIndex: payload.sdp_mline_index ?? null,
        }
        if (this.pc.remoteDescription) await this.pc.addIceCandidate(candidate)
        else this.#pending.push(candidate)
        break
      }
      case 'answer':
      case 'keyframe_request':
        // The host offers; a viewer never receives an answer.
        break
    }
  }

  close(): void {
    this.pc.close()
  }

  requestKeyframe(): void {
    this.#send({ type: 'keyframe_request' })
  }
}

/** One sample of what the viewer is actually getting. */
export interface ViewerStats {
  width: number
  height: number
  /** Frames decoded per second over the last sample interval. */
  fps: number
  /**
   * Frames arriving off the network per second. Independent of rendering, so it
   * stays honest when the tab is hidden and the browser throttles decode.
   */
  receivedFps: number
  /** The page was hidden during the sample, so `fps` reflects throttling. */
  hidden: boolean
  /** Worst one-second fps seen since the stream started. */
  minFps: number
  framesDecoded: number
  videoPacketsReceived?: number | null
  framesPresented?: number | null
  framesDropped: number
  /** Stalls the browser counted (a frame late by >= 150 ms or 3x the average). */
  freezeCount: number
  totalFreezesMs: number
  /** Average time frames spent in the jitter buffer, over the last interval. */
  jitterBufferMs: number
  /** Average decode time per frame, over the last interval. */
  decodeMs: number
  packetsLost: number
  nackCount: number
  pliCount: number
  bitrateKbps: number
  rttMs: number | null
  codec: string
  decoder: string
  audioConcealedPct: number
  /** Null when the browser does not expose the audio receive counters. */
  audioPacketsReceived: number | null
  audioEmittedSamples: number | null
  /** Whether media goes peer to peer or through a TURN relay; null until known. */
  path: 'direct' | 'relay' | null
}

interface Previous {
  at: number
  framesDecoded: number
  framesRendered: number
  framesReceived: number
  bytes: number
  jitterDelay: number
  jitterEmitted: number
  decodeTime: number
  audioSamples: number
  audioConcealed: number
}

/**
 * Turns successive `getStats()` reports into per-interval numbers.
 *
 * The browser reports cumulative counters; deltas between samples are what show
 * a hitch, so the sampler keeps the previous report.
 */
export class StatsSampler {
  #prev: Previous | null = null
  #minFps = Infinity

  async sample(pc: RTCPeerConnection): Promise<ViewerStats | null> {
    const report = await pc.getStats()
    let video: Record<string, number | string> | null = null
    let audio: Record<string, number | string> | null = null
    let pair: Record<string, number | string> | null = null
    const codecs = new Map<string, string>()
    const candidateTypes = new Map<string, string>()

    report.forEach((entry: Record<string, number | string>) => {
      if (entry.type === 'inbound-rtp' && entry.kind === 'video') video = entry
      if (entry.type === 'inbound-rtp' && entry.kind === 'audio') audio = entry
      if (entry.type === 'candidate-pair' && entry.nominated && entry.state === 'succeeded') {
        pair = entry
      }
      if (entry.type === 'codec') codecs.set(String(entry.id), String(entry.mimeType))
      if (entry.type === 'local-candidate' || entry.type === 'remote-candidate') {
        candidateTypes.set(String(entry.id), String(entry.candidateType))
      }
    })
    if (!video) return null
    const v = video as Record<string, number | string>
    const a = audio as Record<string, number | string> | null
    const num = (value: number | string | undefined) => (typeof value === 'number' ? value : 0)

    const now = performance.now()
    const current: Previous = {
      at: now,
      framesDecoded: num(v.framesDecoded),
      framesRendered: num(v.framesRendered ?? v.framesDecoded),
      framesReceived: num(v.framesReceived),
      bytes: num(v.bytesReceived),
      jitterDelay: num(v.jitterBufferDelay),
      jitterEmitted: num(v.jitterBufferEmittedCount),
      decodeTime: num(v.totalDecodeTime),
      audioSamples: audio ? num((audio as Record<string, number>).totalSamplesReceived) : 0,
      audioConcealed: audio ? num((audio as Record<string, number>).concealedSamples) : 0,
    }
    const prev = this.#prev
    this.#prev = current
    if (!prev) return null

    const seconds = (now - prev.at) / 1000
    const rendered = current.framesRendered - prev.framesRendered
    const decoded = current.framesDecoded - prev.framesDecoded
    const fps = rendered / seconds
    // The first seconds include connection setup; only judge a steady stream.
    if (current.framesDecoded > 120) this.#minFps = Math.min(this.#minFps, fps)
    const emitted = current.jitterEmitted - prev.jitterEmitted
    const audioSamples = current.audioSamples - prev.audioSamples

    return {
      width: num(v.frameWidth),
      height: num(v.frameHeight),
      fps,
      receivedFps: (current.framesReceived - prev.framesReceived) / seconds,
      hidden: document.visibilityState === 'hidden',
      minFps: Number.isFinite(this.#minFps) ? this.#minFps : fps,
      framesDecoded: current.framesDecoded,
      videoPacketsReceived: typeof v.packetsReceived === 'number' ? v.packetsReceived : null,
      framesDropped: num(v.framesDropped),
      freezeCount: num(v.freezeCount),
      totalFreezesMs: num(v.totalFreezesDuration) * 1000,
      jitterBufferMs: emitted > 0 ? ((current.jitterDelay - prev.jitterDelay) / emitted) * 1000 : 0,
      decodeMs: decoded > 0 ? ((current.decodeTime - prev.decodeTime) / decoded) * 1000 : 0,
      packetsLost: num(v.packetsLost),
      nackCount: num(v.nackCount),
      pliCount: num(v.pliCount),
      bitrateKbps: ((current.bytes - prev.bytes) * 8) / 1000 / seconds,
      rttMs: pair ? num((pair as Record<string, number>).currentRoundTripTime) * 1000 : null,
      codec: codecs.get(String(v.codecId)) ?? '',
      decoder: String(v.decoderImplementation ?? ''),
      audioConcealedPct:
        audioSamples > 0
          ? ((current.audioConcealed - prev.audioConcealed) / audioSamples) * 100
          : 0,
      audioPacketsReceived: a && typeof a.packetsReceived === 'number' ? a.packetsReceived : null,
      audioEmittedSamples:
        a && typeof a.jitterBufferEmittedCount === 'number' ? a.jitterBufferEmittedCount : null,
      path: pair ? pathOf(pair as Record<string, string>, candidateTypes) : null,
    }
  }
}

/** Repair only proven decode/presentation stalls; an unchanged source may be idle. */
export class VideoWatchdog {
  #previous: { packets: number; decoded: number; presented: number | null } | null = null
  #stalledAt: number | null = null
  #repairAt: number | null = null
  #lastCheck: number | null = null
  #trafficAt: number | null = null
  #decodedAt: number | null = null
  #kind: 'decode' | 'presentation' | null = null

  check(
    sample: ViewerStats,
    enabled: boolean,
    presented: number | null,
    at = performance.now(),
  ): 'keyframe' | 'playback' | 'reconnect' | null {
    const previous = this.#previous
    const gap = this.#lastCheck === null ? 0 : at - this.#lastCheck
    this.#lastCheck = at
    const packets = sample.videoPacketsReceived ?? null
    this.#previous = packets === null ? null : { packets, decoded: sample.framesDecoded, presented }
    // Hidden tabs and timer suspension get a fresh observation window on return.
    if (
      !enabled ||
      sample.hidden ||
      gap > 5000 ||
      !previous ||
      packets === null ||
      packets < previous.packets ||
      sample.framesDecoded < previous.decoded
    ) {
      this.#stalledAt = this.#repairAt = null
      this.#trafficAt = this.#decodedAt = this.#kind = null
      return null
    }
    if (packets > previous.packets) this.#trafficAt = at
    if (sample.framesDecoded > previous.decoded) this.#decodedAt = at
    // Keep the observation window through short packet bursts, including 1 fps
    // idle refreshes; a single empty sample must not erase an ongoing stall.
    const decodeStuck =
      this.#trafficAt !== null &&
      at - this.#trafficAt < 3000 &&
      sample.framesDecoded === previous.decoded
    const presentationStuck =
      this.#decodedAt !== null &&
      at - this.#decodedAt < 3000 &&
      presented !== null &&
      previous.presented !== null &&
      presented === previous.presented
    const kind = decodeStuck ? 'decode' : presentationStuck ? 'presentation' : null
    if (kind !== this.#kind) {
      this.#stalledAt = this.#repairAt = null
      this.#kind = kind
    }
    if (kind === null) return null
    this.#stalledAt ??= at
    if (at - this.#stalledAt < 5000) return null
    if (this.#repairAt === null) {
      this.#repairAt = at
      return kind === 'decode' ? 'keyframe' : 'playback'
    }
    return at - this.#repairAt >= 5000 ? 'reconnect' : null
  }
}

/** Detect a stuck audio receiver without treating quiet source audio as failure. */
export class AudioWatchdog {
  #previous: { packets: number; emitted: number } | null = null
  #stalledAt: number | null = null

  check(sample: ViewerStats, enabled: boolean, at = performance.now()): boolean {
    const packets = sample.audioPacketsReceived
    const emitted = sample.audioEmittedSamples
    const previous = this.#previous
    this.#previous = packets === null || emitted === null ? null : { packets, emitted }
    // Silence still emits decoded samples. No packets can mean an idle capture
    // source, so only repair when packets arrive but audio stops being emitted.
    if (
      !enabled ||
      sample.hidden ||
      sample.receivedFps <= 0 ||
      packets === null ||
      emitted === null ||
      !previous ||
      packets <= previous.packets ||
      emitted !== previous.emitted
    ) {
      this.#stalledAt = null
      return false
    }
    this.#stalledAt ??= at
    return at - this.#stalledAt >= 8000
  }
}

function pathOf(
  pair: Record<string, string>,
  candidateTypes: Map<string, string>,
): 'direct' | 'relay' | null {
  const local = candidateTypes.get(pair.localCandidateId)
  const remote = candidateTypes.get(pair.remoteCandidateId)
  if (!local && !remote) return null
  return local === 'relay' || remote === 'relay' ? 'relay' : 'direct'
}
